import React, { useState, useEffect } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Avatar from '@mui/material/Avatar';
import Link from '@mui/material/Link';
import ButtonGroup from '@mui/material/ButtonGroup';
import Pagination from '@mui/material/Pagination'; // Import Pagination component
import { useNavigate } from "react-router-dom";
import Swal from 'sweetalert2';
import { deleteMedia } from '../api/wordpressApi';
import { syncGemToWoo } from '../api/wooCommerceApi';
import { updateGem } from '../api/backendApi';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Grid from '@mui/material/Grid';
import { gemtypeLists, shapeLists, colorLists, clarityLists, originLists, treatmentLists } from '../utils/Utils';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ListAltIcon from '@mui/icons-material/ListAlt';
import Stack from '@mui/material/Stack';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Chip from '@mui/material/Chip';
import FilterListIcon from '@mui/icons-material/FilterList';
import Drawer from '@mui/material/Drawer';
import CloseIcon from '@mui/icons-material/Close';
import Divider from '@mui/material/Divider';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ClearIcon from '@mui/icons-material/Clear';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Menu from '@mui/material/Menu';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import SyncIcon from '@mui/icons-material/Sync';
import LinearProgress from '@mui/material/LinearProgress';
import Alert from '@mui/material/Alert';

const DEFAULT_COLUMNS = { sku: true, avatar: true, type: true, ctw: true, shape: true, dimension: true, cert: true, seller: true, website: true };

export default function Gems() {
    // ===== Hooks and State Management =====
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    // Loading and Data States
    const [isLoaded, setIsLoaded] = useState(true);
    const [user, setUser] = useState([]);
    const [items, setItems] = useState([]);
    const [filteredItems, setFilteredItems] = useState([]);
    
    // Filter States with localStorage persistence
    const [type, setType] = useState(() => localStorage.getItem('gemFilter_type') || '');
    const [shape, setShape] = useState(() => localStorage.getItem('gemFilter_shape') || '');
    const [color, setColor] = useState(() => localStorage.getItem('gemFilter_color') || '');
    const [clarity, setClarity] = useState(() => localStorage.getItem('gemFilter_clarity') || '');
    const [origin, setOrigin] = useState(() => localStorage.getItem('gemFilter_origin') || '');
    const [treatment, setTreatment] = useState(() => localStorage.getItem('gemFilter_treatment') || '');
    const [certificate, setCertificate] = useState(() => {
        const saved = localStorage.getItem('gemFilter_certificate');
        return saved ? saved.split(',') : [];
    });
    const [seller, setSeller] = useState(() => localStorage.getItem('gemFilter_seller') || '');
    const [priceOption, setPriceOption] = useState(() => localStorage.getItem('gemFilter_priceOption') || '');
    const [wooStatus, setWooStatus] = useState(() => localStorage.getItem('gemFilter_wooStatus') || '');

    // Pagination States
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [rowsPerPageOptions] = useState([5, 10, 25, 50, 100]);

    // UI States
    const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

    // Column Visibility State
    const [visibleColumns, setVisibleColumns] = useState(() => {
        try {
            const saved = localStorage.getItem('gemTable_columns');
            return saved ? { ...DEFAULT_COLUMNS, ...JSON.parse(saved) } : DEFAULT_COLUMNS;
        } catch { return DEFAULT_COLUMNS; }
    });
    const [columnMenuAnchor, setColumnMenuAnchor] = useState(null);

    // Sync States
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [isSyncing, setIsSyncing] = useState(false);
    const [syncProgress, setSyncProgress] = useState({ done: 0, total: 0, errors: [] });

    // ===== Effects =====
    // Initial authentication check
    useEffect(() => {
        checkAuth();
    }, []);

    // Filter items when filters change
    useEffect(() => {
        filterItems();
    }, [items, type, shape, color, clarity, origin, treatment]);

    // Add useEffect for seller filter
    useEffect(() => {
        filterItems();
    }, [seller]);

    // Add useEffect for wooStatus filter
    useEffect(() => {
        filterItems();
    }, [wooStatus]);

    // Save filter states to localStorage
    useEffect(() => { localStorage.setItem('gemFilter_type', type); }, [type]);
    useEffect(() => { localStorage.setItem('gemFilter_shape', shape); }, [shape]);
    useEffect(() => { localStorage.setItem('gemFilter_color', color); }, [color]);
    useEffect(() => { localStorage.setItem('gemFilter_clarity', clarity); }, [clarity]);
    useEffect(() => { localStorage.setItem('gemFilter_origin', origin); }, [origin]);
    useEffect(() => { localStorage.setItem('gemFilter_treatment', treatment); }, [treatment]);
    useEffect(() => { localStorage.setItem('gemFilter_certificate', certificate.join(',')); }, [certificate]);
    useEffect(() => { localStorage.setItem('gemFilter_seller', seller); }, [seller]);
    useEffect(() => { localStorage.setItem('gemFilter_priceOption', priceOption); }, [priceOption]);
    useEffect(() => { localStorage.setItem('gemFilter_wooStatus', wooStatus); }, [wooStatus]);
    useEffect(() => { localStorage.setItem('gemTable_columns', JSON.stringify(visibleColumns)); }, [visibleColumns]);

    // ===== Data Fetching Functions =====
    /**
     * Fetches gem data from the API
     * Updates the items and filteredItems state
     */
    const gemGet = () => {
        fetch("https://app.gemnoble.com:2005/gems")
            .then(res => res.json())
            .then((result) => {
                setItems(result);
                setFilteredItems(result);
                setIsLoaded(false);
            });
    }

    /**
     * Checks if user is authenticated
     * Redirects to signin if not authenticated
     */
    const checkAuth = () => {
        const isAuthenticated = localStorage.getItem('isAuthenticated');
        if (isAuthenticated === 'true') {
            gemGet();
        } else {
            Swal.fire({
                icon: "error",
                text: "Please login first"
            }).then(() => {
                navigate('/signin');
            });
        }
    }

    // ===== Media Management Functions =====
    /**
     * Deletes images from WordPress media library
     * @param {Array} images - Array of image objects to delete
     */
    const handleDeleteImages = async (images) => {
        for (let i = 0; i < images.length; i++) {
            await deleteMedia(images[i].id);
        }
        console.log("Deleted images");
    };

    /**
     * Deletes videos from WordPress media library
     * @param {Array} videos - Array of video objects to delete
     */
    const handleDeleteVideos = async (videos) => {
        for (let i = 0; i < videos.length; i++) {
            await deleteMedia(videos[i].id);
        }
        console.log("Deleted videos");
    };

    /**
     * Deletes certificates from WordPress media library
     * @param {Array} certs - Array of certificate objects to delete
     */
    const handleDeleteCerts = async (certs) => {
        for (let i = 0; i < certs.length; i++) {
            await deleteMedia(certs[i].id);
        }
        console.log("Deleted certimgs");
    };

    // ===== Filter Functions =====
    /**
     * Filters items based on selected criteria
     * Updates filteredItems state
     */
    const filterItems = () => {
        let filtered = [...items];
        
        if (type) filtered = filtered.filter(item => item.type === type);
        if (shape) filtered = filtered.filter(item => item.shape === shape);
        if (color) filtered = filtered.filter(item => item.color === color);
        if (clarity) filtered = filtered.filter(item => item.clarity === clarity);
        if (origin) filtered = filtered.filter(item => item.origin === origin);
        if (treatment) filtered = filtered.filter(item => item.treatment === treatment);
        if (seller) filtered = filtered.filter(item => item.seller === seller);
        if (wooStatus === 'not_listed') filtered = filtered.filter(item => item.woo === null || item.woo === undefined);
        else if (wooStatus === 'publish') filtered = filtered.filter(item => item.woo?.status === 'publish');
        else if (wooStatus === 'private') filtered = filtered.filter(item => item.woo?.status === 'private');

        setFilteredItems(filtered);
        setPage(1); // Reset to first page when filters change
    };

    /**
     * Clears all filter states and localStorage
     */
    const clearAllFilters = () => {
        setType('');
        setShape('');
        setColor('');
        setClarity('');
        setOrigin('');
        setTreatment('');
        setCertificate([]);
        setSeller('');
        setPriceOption('');
        setWooStatus('');
        
        // Clear localStorage
        localStorage.removeItem('gemFilter_type');
        localStorage.removeItem('gemFilter_shape');
        localStorage.removeItem('gemFilter_color');
        localStorage.removeItem('gemFilter_clarity');
        localStorage.removeItem('gemFilter_origin');
        localStorage.removeItem('gemFilter_treatment');
        localStorage.removeItem('gemFilter_certificate');
        localStorage.removeItem('gemFilter_seller');
        localStorage.removeItem('gemFilter_priceOption');
        localStorage.removeItem('gemFilter_wooStatus');
    };

    const toggleColumn = (col) => {
        setVisibleColumns(prev => ({ ...prev, [col]: !prev[col] }));
    };

    // ===== Sync Functions =====
    const toggleSelectId = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const toggleSelectPage = () => {
        const pageIds = paginatedItems.filter(r => r.woo?.id).map(r => r.id);
        const allSelected = pageIds.every(id => selectedIds.has(id));
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (allSelected) pageIds.forEach(id => next.delete(id));
            else pageIds.forEach(id => next.add(id));
            return next;
        });
    };

    const handleSyncSelected = async (idsToSync) => {
        const gems = items.filter(g => idsToSync.has(g.id) && g.woo?.id);
        if (gems.length === 0) return;
        setIsSyncing(true);
        setSyncProgress({ done: 0, total: gems.length, errors: [] });
        const errors = [];
        for (let i = 0; i < gems.length; i++) {
            try {
                const wooData = await syncGemToWoo(gems[i]);
                const raw = JSON.stringify({ ...gems[i], woo: wooData });
                await updateGem(raw);
                // Update local items state
                setItems(prev => prev.map(g => g.id === gems[i].id ? { ...g, woo: wooData } : g));
            } catch (err) {
                errors.push(`${gems[i].id}: ${err.message}`);
            }
            setSyncProgress({ done: i + 1, total: gems.length, errors });
        }
        setIsSyncing(false);
        setSelectedIds(new Set());
    };

    // ===== Delete Function =====
    /**
     * Deletes a gem and its associated media
     * @param {Object} gem - The gem object to delete
     */
    const UserDelete = (gem) => {
        if (gem.woo == null) {
            handleDeleteImages(gem.images);
            handleDeleteVideos(gem.videos);
            handleDeleteCerts(gem.certimgs);
            
            var myHeaders = new Headers();
            myHeaders.append("Content-Type", "application/json");
            var raw = JSON.stringify({ "id": gem.id });
            var requestOptions = {
                method: 'DELETE',
                headers: myHeaders,
                body: raw,
                redirect: 'follow'
            };
            
            fetch("https://app.gemnoble.com:2005/gems/delete/", requestOptions)
                .then(response => response.json())
                .then(result => {
                    if (result['status'] === 'ok')
                        checkAuth();
                })
                .catch(error => console.log('error', error));
        } else if (gem.woo != null) {
            Swal.fire({
                icon: "error",
                text: "Unable to Delete Because it is now Live on Website Link: " + gem.woo.permalink
            });
        }
    }

    // ===== Pagination Handlers =====
    /**
     * Handles page change in pagination
     * @param {Object} event - The event object
     * @param {number} newPage - The new page number
     */
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    /**
     * Handles rows per page change in pagination
     * @param {Object} event - The event object
     */
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(1);
    };

    // Calculate paginated items
    const paginatedItems = filteredItems.slice((page - 1) * rowsPerPage, page * rowsPerPage);

    // ===== UI Handlers =====
    /**
     * Toggles the filter drawer open/closed
     */
    const toggleFilterDrawer = () => {
        setIsFilterDrawerOpen(!isFilterDrawerOpen);
    };

    return (
        <React.Fragment>
            <CssBaseline />
            <Container maxWidth="lg" sx={{ p: isMobile ? 1 : 2 }}>
                <Paper sx={{ p: isMobile ? 1 : 2 }}>
                    <Box display="flex" sx={{ mb: 2 }} alignItems="center">
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="h6" gutterBottom={!isMobile}>
                                Gems
                            </Typography>
                        </Box>
                        
                        {isMobile && (
                            <Button
                                startIcon={<FilterListIcon />}
                                onClick={toggleFilterDrawer}
                                sx={{ mr: 1 }}
                                variant="outlined"
                            >
                                Filters
                            </Button>
                        )}
                    
                        {!isMobile && (
                            <Tooltip title="Show/Hide Columns">
                                <IconButton onClick={(e) => setColumnMenuAnchor(e.currentTarget)} sx={{ mr: 1 }}>
                                    <ViewColumnIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Menu
                            anchorEl={columnMenuAnchor}
                            open={Boolean(columnMenuAnchor)}
                            onClose={() => setColumnMenuAnchor(null)}
                        >
                            {[
                                { key: 'sku', label: 'SKU' },
                                { key: 'avatar', label: 'Avatar' },
                                { key: 'type', label: 'Type' },
                                { key: 'ctw', label: 'CTW' },
                                { key: 'shape', label: 'Shape' },
                                { key: 'dimension', label: 'Dimension' },
                                { key: 'cert', label: 'Cert' },
                                { key: 'seller', label: 'Seller' },
                                { key: 'website', label: 'Website' },
                            ].map(col => (
                                <MenuItem key={col.key} onClick={() => toggleColumn(col.key)} dense>
                                    <FormControlLabel
                                        control={<Checkbox checked={visibleColumns[col.key]} size="small" />}
                                        label={col.label}
                                        sx={{ m: 0 }}
                                    />
                                </MenuItem>
                            ))}
                        </Menu>
                        <Box>
                            <Link href="create" style={{ textDecoration: 'none' }}>
                                <Button variant="contained" size={isMobile ? "small" : "medium"}>
                                    Create
                                </Button>
                            </Link>
                        </Box>
                    </Box>

                    {/* Sync Toolbar */}
                    {!isMobile && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Button
                                variant="outlined"
                                startIcon={<SyncIcon />}
                                disabled={selectedIds.size === 0 || isSyncing}
                                onClick={() => handleSyncSelected(selectedIds)}
                                size="small"
                            >
                                Sync Selected ({selectedIds.size})
                            </Button>
                            <Button
                                variant="outlined"
                                color="secondary"
                                startIcon={<SyncIcon />}
                                disabled={isSyncing}
                                onClick={() => {
                                    const allListedIds = new Set(items.filter(g => g.woo?.id).map(g => g.id));
                                    handleSyncSelected(allListedIds);
                                }}
                                size="small"
                            >
                                Sync All ({items.filter(g => g.woo?.id).length})
                            </Button>
                            {isSyncing && (
                                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <LinearProgress sx={{ flex: 1 }} variant="determinate" value={(syncProgress.done / syncProgress.total) * 100} />
                                    <Typography variant="body2" color="text.secondary" noWrap>
                                        {syncProgress.done}/{syncProgress.total}
                                    </Typography>
                                </Box>
                            )}
                            {!isSyncing && syncProgress.done > 0 && (
                                <Alert severity={syncProgress.errors.length > 0 ? 'warning' : 'success'} onClose={() => setSyncProgress({ done: 0, total: 0, errors: [] })} sx={{ py: 0 }}>
                                    Synced {syncProgress.done - syncProgress.errors.length}/{syncProgress.total}
                                    {syncProgress.errors.length > 0 ? ` (${syncProgress.errors.length} failed)` : ''}
                                </Alert>
                            )}
                        </Box>
                    )}

                    {/* Desktop Filters */}
                    {!isMobile && (
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={12}>
                                <Paper 
                                    elevation={2} 
                                    sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        backgroundColor: 'background.default'
                                    }}
                                >
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Typography 
                                            variant='h6' 
                                            component='div'
                                            sx={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                gap: 1,
                                                color: 'primary.main'
                                            }}
                                        >
                                            <FilterListIcon color="primary" />
                                            Filters
                                        </Typography>
                                        <Button
                                            variant="outlined"
                                            color="secondary"
                                            startIcon={<ClearIcon />}
                                            onClick={clearAllFilters}
                                        >
                                            Clear All Filters
                                        </Button>
                                    </Box>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={4} md={2}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Seller</InputLabel>
                                                <Select
                                                    value={seller}
                                                    label="Seller"
                                                    onChange={(e) => setSeller(e.target.value)}
                                                >
                                                    <MenuItem value="">All</MenuItem>
                                                    {Array.from(new Set(items.map(item => item.seller))).map((seller) => (
                                                        <MenuItem key={seller} value={seller}>
                                                            {seller}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={4} md={2}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Type</InputLabel>
                                                <Select
                                                    value={type}
                                                    label="Type"
                                                    onChange={(e) => setType(e.target.value)}
                                                >
                                                    <MenuItem value="">All</MenuItem>
                                                    {gemtypeLists.map((type) => (
                                                        <MenuItem key={type.key} value={type.key}>
                                                            {type.value}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={4} md={2}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Shape</InputLabel>
                                                <Select
                                                    value={shape}
                                                    label="Shape"
                                                    onChange={(e) => setShape(e.target.value)}
                                                >
                                                    <MenuItem value="">All</MenuItem>
                                                    {shapeLists.map((shape) => (
                                                        <MenuItem key={shape.key} value={shape.key}>
                                                            {shape.value}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={4} md={2}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Color</InputLabel>
                                                <Select
                                                    value={color}
                                                    label="Color"
                                                    onChange={(e) => setColor(e.target.value)}
                                                >
                                                    <MenuItem value="">All</MenuItem>
                                                    {colorLists.map((color) => (
                                                        <MenuItem key={color.key} value={color.key}>
                                                            {color.value}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={4} md={2}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Clarity</InputLabel>
                                                <Select
                                                    value={clarity}
                                                    label="Clarity"
                                                    onChange={(e) => setClarity(e.target.value)}
                                                >
                                                    <MenuItem value="">All</MenuItem>
                                                    {clarityLists.map((clarity) => (
                                                        <MenuItem key={clarity.key} value={clarity.key}>
                                                            {clarity.value}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={4} md={2}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Origin</InputLabel>
                                                <Select
                                                    value={origin}
                                                    label="Origin"
                                                    onChange={(e) => setOrigin(e.target.value)}
                                                >
                                                    <MenuItem value="">All</MenuItem>
                                                    {originLists.map((origin) => (
                                                        <MenuItem key={origin.key} value={origin.key}>
                                                            {origin.value}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={4} md={2}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Treatment</InputLabel>
                                                <Select
                                                    value={treatment}
                                                    label="Treatment"
                                                    onChange={(e) => setTreatment(e.target.value)}
                                                >
                                                    <MenuItem value="">All</MenuItem>
                                                    {treatmentLists.map((treatment) => (
                                                        <MenuItem key={treatment.key} value={treatment.key}>
                                                            {treatment.value}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={4} md={2}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Website Status</InputLabel>
                                                <Select
                                                    value={wooStatus}
                                                    label="Website Status"
                                                    onChange={(e) => setWooStatus(e.target.value)}
                                                >
                                                    <MenuItem value="">All</MenuItem>
                                                    <MenuItem value="publish">Published</MenuItem>
                                                    <MenuItem value="private">Private</MenuItem>
                                                    <MenuItem value="not_listed">Not Listed</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Grid>
                        </Grid>
                    )}

                    {/* Mobile Filter Drawer */}
                    <Drawer
                        anchor="right"
                        open={isFilterDrawerOpen}
                        onClose={toggleFilterDrawer}
                        PaperProps={{
                            sx: { width: '80%', maxWidth: '300px', p: 2 }
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">Filters</Typography>
                            <IconButton onClick={toggleFilterDrawer}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        <Stack spacing={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Seller</InputLabel>
                                <Select
                                    value={seller}
                                    label="Seller"
                                    onChange={(e) => setSeller(e.target.value)}
                                >
                                    <MenuItem value="">All</MenuItem>
                                    {Array.from(new Set(items.map(item => item.seller))).map((seller) => (
                                        <MenuItem key={seller} value={seller}>
                                            {seller}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth size="small">
                                <InputLabel>Type</InputLabel>
                                <Select
                                    value={type}
                                    label="Type"
                                    onChange={(e) => setType(e.target.value)}
                                >
                                    <MenuItem value="">All</MenuItem>
                                    {gemtypeLists.map((type) => (
                                        <MenuItem key={type.key} value={type.key}>
                                            {type.value}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth size="small">
                                <InputLabel>Shape</InputLabel>
                                <Select
                                    value={shape}
                                    label="Shape"
                                    onChange={(e) => setShape(e.target.value)}
                                >
                                    <MenuItem value="">All</MenuItem>
                                    {shapeLists.map((shape) => (
                                        <MenuItem key={shape.key} value={shape.key}>
                                            {shape.value}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth size="small">
                                <InputLabel>Color</InputLabel>
                                <Select
                                    value={color}
                                    label="Color"
                                    onChange={(e) => setColor(e.target.value)}
                                >
                                    <MenuItem value="">All</MenuItem>
                                    {colorLists.map((color) => (
                                        <MenuItem key={color.key} value={color.key}>
                                            {color.value}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth size="small">
                                <InputLabel>Clarity</InputLabel>
                                <Select
                                    value={clarity}
                                    label="Clarity"
                                    onChange={(e) => setClarity(e.target.value)}
                                >
                                    <MenuItem value="">All</MenuItem>
                                    {clarityLists.map((clarity) => (
                                        <MenuItem key={clarity.key} value={clarity.key}>
                                            {clarity.value}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth size="small">
                                <InputLabel>Origin</InputLabel>
                                <Select
                                    value={origin}
                                    label="Origin"
                                    onChange={(e) => setOrigin(e.target.value)}
                                >
                                    <MenuItem value="">All</MenuItem>
                                    {originLists.map((origin) => (
                                        <MenuItem key={origin.key} value={origin.key}>
                                            {origin.value}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth size="small">
                                <InputLabel>Treatment</InputLabel>
                                <Select
                                    value={treatment}
                                    label="Treatment"
                                    onChange={(e) => setTreatment(e.target.value)}
                                >
                                    <MenuItem value="">All</MenuItem>
                                    {treatmentLists.map((treatment) => (
                                        <MenuItem key={treatment.key} value={treatment.key}>
                                            {treatment.value}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth size="small">
                                <InputLabel>Website Status</InputLabel>
                                <Select
                                    value={wooStatus}
                                    label="Website Status"
                                    onChange={(e) => setWooStatus(e.target.value)}
                                >
                                    <MenuItem value="">All</MenuItem>
                                    <MenuItem value="publish">Published</MenuItem>
                                    <MenuItem value="private">Private</MenuItem>
                                    <MenuItem value="not_listed">Not Listed</MenuItem>
                                </Select>
                            </FormControl>
                        </Stack>
                    </Drawer>

                    {/* Mobile Card View / Desktop Table View */}
                    {isMobile ? (
                        <Stack spacing={2}>
                            {paginatedItems.map((row) => (
                                <Card key={row.id}>
                                    <CardContent>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <Avatar 
                                                alt={row.username} 
                                                src={row.images[0].media_details.sizes.thumbnail.source_url}
                                                sx={{ width: 60, height: 60, mr: 2 }}
                                            />
                                            <Box>
                                                <Typography variant="subtitle1" gutterBottom>
                                                    SKU: {row.id}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {row.type} - {row.weight} ct
                                                </Typography>
                                            </Box>
                                        </Box>
                                        
                                        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                                            <Chip label={`Shape: ${row.shape}`} size="small" />
                                            <Chip label={`${row.width} x ${row.length} x ${row.depth}`} size="small" />
                                            <Chip label={`Cert: ${row.certificate}`} size="small" />
                                            <Chip label={`Seller: ${row.seller}`} size="small" />
                                            {row.woo === null || row.woo === undefined
                                                ? <Chip label="Not Listed" size="small" color="default" />
                                                : row.woo.status === 'publish'
                                                    ? <Chip label="Published" size="small" color="success" />
                                                    : <Chip label="Private" size="small" color="warning" />
                                            }
                                        </Stack>

                                        <Stack direction="row" spacing={1} justifyContent="center">
                                            <Tooltip title="View Listing">
                                                <IconButton 
                                                    onClick={() => window.location = '/listing/' + row.id}
                                                    size="small"
                                                    sx={{
                                                        color: 'info.main',
                                                        '&:hover': {
                                                            backgroundColor: 'info.light',
                                                            color: 'white',
                                                        },
                                                    }}
                                                >
                                                    <ListAltIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Preview Description">
                                                <IconButton 
                                                    onClick={() => navigate('/preview/' + row.id)}
                                                    size="small"
                                                    sx={{
                                                        color: 'success.main',
                                                        '&:hover': {
                                                            backgroundColor: 'success.light',
                                                            color: 'white',
                                                        },
                                                    }}
                                                >
                                                    <VisibilityIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Edit">
                                                <IconButton 
                                                    onClick={() => window.location = '/update/' + row.id}
                                                    size="small"
                                                    sx={{
                                                        color: 'primary.main',
                                                        '&:hover': {
                                                            backgroundColor: 'primary.light',
                                                            color: 'white',
                                                        },
                                                    }}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={row.woo === null ? "Delete" : "Cannot delete - Item is live"}>
                                                <span>
                                                    <IconButton 
                                                        onClick={() => UserDelete(row)}
                                                        disabled={!(row.woo === null)}
                                                        size="small"
                                                        sx={{
                                                            color: 'error.main',
                                                            '&:hover': {
                                                                backgroundColor: 'error.light',
                                                                color: 'white',
                                                            },
                                                            '&.Mui-disabled': {
                                                                color: 'action.disabled',
                                                                backgroundColor: 'action.disabledBackground',
                                                            },
                                                        }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            ))}
                        </Stack>
                    ) : (
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 650 }} aria-label="simple table">
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            size="small"
                                            indeterminate={paginatedItems.filter(r => r.woo?.id).some(r => selectedIds.has(r.id)) && !paginatedItems.filter(r => r.woo?.id).every(r => selectedIds.has(r.id))}
                                            checked={paginatedItems.filter(r => r.woo?.id).length > 0 && paginatedItems.filter(r => r.woo?.id).every(r => selectedIds.has(r.id))}
                                            onChange={toggleSelectPage}
                                        />
                                    </TableCell>
                                    {visibleColumns.sku && <TableCell>SKU</TableCell>}
                                    {visibleColumns.avatar && <TableCell align="center">Avatar</TableCell>}
                                    {visibleColumns.type && <TableCell align="right">Type</TableCell>}
                                    {visibleColumns.ctw && <TableCell align="right">CTW</TableCell>}
                                    {visibleColumns.shape && <TableCell align="right">Shape</TableCell>}
                                    {visibleColumns.dimension && <TableCell align="right">Dimension</TableCell>}
                                    {visibleColumns.cert && <TableCell align="right">Cert</TableCell>}
                                    {visibleColumns.seller && <TableCell align="right">Seller</TableCell>}
                                    {visibleColumns.website && <TableCell align="center">Website</TableCell>}
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedItems.map((row) => (
                                    <TableRow
                                        key={row.id}
                                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                    >
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                size="small"
                                                disabled={!row.woo?.id}
                                                checked={selectedIds.has(row.id)}
                                                onChange={() => toggleSelectId(row.id)}
                                            />
                                        </TableCell>
                                        {visibleColumns.sku && <TableCell component="th" scope="row">{row.id}</TableCell>}
                                        {visibleColumns.avatar && (
                                            <TableCell align="center">
                                                <Box display="flex" justifyContent="center">
                                                    <Avatar alt={row.username} src={row.images[0].media_details.sizes.thumbnail.source_url} />
                                                </Box>
                                            </TableCell>
                                        )}
                                        {visibleColumns.type && <TableCell align="right">{row.type}</TableCell>}
                                        {visibleColumns.ctw && <TableCell align="right">{row.weight}</TableCell>}
                                        {visibleColumns.shape && <TableCell align="right">{row.shape}</TableCell>}
                                        {visibleColumns.dimension && <TableCell align="right">{row.width + " x " + row.length + " x " + row.depth}</TableCell>}
                                        {visibleColumns.cert && <TableCell align="right">{row.certificate.toString()}</TableCell>}
                                        {visibleColumns.seller && <TableCell align="right">{row.seller}</TableCell>}
                                        {visibleColumns.website && (
                                            <TableCell align="center">
                                                {row.woo === null || row.woo === undefined
                                                    ? <Chip label="Not Listed" size="small" color="default" />
                                                    : row.woo.status === 'publish'
                                                        ? <Chip label="Published" size="small" color="success" />
                                                        : <Chip label="Private" size="small" color="warning" />
                                                }
                                            </TableCell>
                                        )}
                                        <TableCell align="center">
                                            <Stack direction="row" spacing={1} justifyContent="center">
                                                <Tooltip title="View Listing">
                                                    <IconButton 
                                                        onClick={() => window.location = '/listing/' + row.id}
                                                        size="small"
                                                        sx={{
                                                            color: 'info.main',
                                                            '&:hover': {
                                                                backgroundColor: 'info.light',
                                                                color: 'white',
                                                            },
                                                        }}
                                                    >
                                                        <ListAltIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Preview Description">
                                                    <IconButton 
                                                        onClick={() => navigate('/preview/' + row.id)}
                                                        size="small"
                                                        sx={{
                                                            color: 'success.main',
                                                            '&:hover': {
                                                                backgroundColor: 'success.light',
                                                                color: 'white',
                                                            },
                                                        }}
                                                    >
                                                        <VisibilityIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Edit">
                                                    <IconButton 
                                                        onClick={() => window.location = '/update/' + row.id}
                                                        size="small"
                                                        sx={{
                                                            color: 'primary.main',
                                                            '&:hover': {
                                                                backgroundColor: 'primary.light',
                                                                color: 'white',
                                                            },
                                                        }}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={row.woo === null ? "Delete" : "Cannot delete - Item is live"}>
                                                    <span>
                                                        <IconButton 
                                                            onClick={() => UserDelete(row)}
                                                            disabled={!(row.woo === null)}
                                                            size="small"
                                                            sx={{
                                                                color: 'error.main',
                                                                '&:hover': {
                                                                    backgroundColor: 'error.light',
                                                                    color: 'white',
                                                                },
                                                                '&.Mui-disabled': {
                                                                    color: 'action.disabled',
                                                                    backgroundColor: 'action.disabledBackground',
                                                                },
                                                            }}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    )}
                    
                    <Box 
                        display="flex" 
                        justifyContent="center" 
                        alignItems="center" 
                        marginTop={2}
                        flexDirection={isMobile ? "column" : "row"}
                        gap={2}
                    >
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                            <Select
                                value={rowsPerPage}
                                onChange={handleChangeRowsPerPage}
                                displayEmpty
                                variant="outlined"
                            >
                                {rowsPerPageOptions.map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {option} items per page
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Pagination
                            count={Math.ceil(filteredItems.length / rowsPerPage)}
                            page={page}
                            onChange={handleChangePage}
                            color="primary"
                            size={isMobile ? "small" : "medium"}
                        />
                    </Box>
                </Paper>
            </Container>
        </React.Fragment>
    );
}
